import { FileText } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useSubmitFeedbackMutation, useGetModulesQuery, useGetPublicArticlesQuery } from '../../store/apis/helpCenter.api';
import { Select, TextArea, Button } from '../common';
import { scrollToTop } from '../survey/hooks';
import toast from 'react-hot-toast';

interface HelpContactFormProps {
    onBack: () => void;
    initialModuleCode?: string;
    initialArticleId?: string;
    isLocked?: boolean;
}

/** Feedback form rendered inside the Figma Submit Feedback modal. Logic unchanged. */
export function HelpContactForm({ onBack, initialModuleCode, initialArticleId, isLocked }: HelpContactFormProps) {
    useEffect(() => {
        scrollToTop();
    }, []);

    const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
    const { data: allModules = [], isLoading: isModulesLoading } = useGetModulesQuery();
    const { data: publicArticlesData, isLoading: isArticlesLoading } = useGetPublicArticlesQuery();

    const modules = useMemo(() => {
        if (!publicArticlesData) return [];
        return allModules.filter(m => publicArticlesData[m.code] && publicArticlesData[m.code].length > 0);
    }, [allModules, publicArticlesData]);


    const [formData, setFormData] = useState({
        moduleCode: initialModuleCode || '',
        articleId: initialArticleId || '',
        description: '',
    });

    const currentArticles = useMemo(() => {
        if (!formData.moduleCode || !publicArticlesData) return [];
        return publicArticlesData[formData.moduleCode] || [];
    }, [formData.moduleCode, publicArticlesData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (!formData.moduleCode) {
                toast.error("Please select a module");
                return;
            }
            if (!formData.description) {
                toast.error("Please provide a description");
                return;
            }

            await submitFeedback({
                moduleCode: formData.moduleCode,
                articleId: formData.articleId,
                description: formData.description,
            }).unwrap();

            toast.success("Feedback submitted successfully");
            onBack();
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            toast.error("Failed to submit feedback");
        }
    };


    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Module */}
                <div>
                    <Select
                        label="Module"
                        required
                        value={formData.moduleCode}
                        onChange={(value) => {
                            setFormData({
                                ...formData,
                                moduleCode: String(value),
                                articleId: '' // Reset article when module changes
                            });
                        }}
                        options={modules.map(m => ({
                            label: m.name,
                            value: m.code,
                        }))}
                        placeholder="Select module"
                        className='w-full'
                        isLoading={isModulesLoading}
                        disabled={isLocked}
                    />
                </div>

                {/* Article */}
                <div>
                    <Select
                        label="Article"
                        value={formData.articleId}
                        onChange={(value) => setFormData({ ...formData, articleId: String(value) })}
                        options={currentArticles.map(a => ({
                            label: a.title,
                            value: a._id,
                        }))}
                        placeholder={formData.moduleCode ? "Select article" : "Select a module first"}
                        className='w-full'
                        isLoading={isArticlesLoading}
                        disabled={isLocked || !formData.moduleCode}
                    />
                </div>
            </div>

            {/* Description */}
            <div>
                <TextArea
                    label="Description"
                    required
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value as string })}
                    rows={6}
                    placeholder="Please provide as much detail as possible about your issue or question..."
                />
            </div>

            {/* Footer Row: Buttons & Info */}
            <div className="flex items-center gap-4 w-full md:w-auto justify-end">
                <Button
                    htmlType="button"
                    appearance="secondary"
                    onClick={onBack}
                    className="w-full md:w-auto"
                >
                    Cancel
                </Button>
                <Button
                    htmlType="submit"
                    onClick={(e) => {
                        // Ensure it fires even if htmlType mapping is swallowed
                        handleSubmit(e as any);
                    }}
                    disabled={isLoading || isModulesLoading}
                    appearance="primary"
                    icon={<FileText className="w-5 h-5" />}
                    className="w-full md:w-auto"
                >
                    {isLoading ? 'Submitting...' : 'Submit Feedback'}
                </Button>
            </div>

            <div className="text-left border-t border-[var(--color-border-tertiary)] pt-4">
                <p className="text-sm text-[var(--color-text-primary)] leading-relaxed">
                    Our support team will review your feedback and get back to you shortly.
                </p>
            </div>
        </form>
    );
}

export default HelpContactForm;
